import * as Y from 'yjs';
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from '../wiring/canvas-roots';

export interface ReconnectArgs {
  edgeId: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
}

/**
 * Updates an edge's source/target in `doc.getMap('edges')` inside a
 * single transaction tagged with the clientId. Used by React Flow's
 * onReconnect when the user drags an endpoint circle to a different
 * node. Self-loops are rejected (no-op).
 *
 * Handle ids are normalised the same way createEdgeFromConnection does
 * — the user-facing source-handle is always the always-mounted drop-{dir}
 * variant, so old `exp-{dir}` ids get rewritten on reconnect too.
 */
export function reconnectEdgeYjs(
  doc: Y.Doc,
  clientId: number,
  args: ReconnectArgs,
  binding: CanvasBinding = TOP_LEVEL_BINDING,
): boolean {
  if (!args.source || !args.target) return false;
  if (args.source === args.target) return false;

  const remap = (id: string | null | undefined): string | null => {
    if (typeof id !== 'string') return null;
    if (id.startsWith('exp-')) return `drop-${id.slice(4)}`;
    return id;
  };

  let written = false;
  doc.transact(() => {
    const edge = canvasRoots(doc, binding).edges.get(args.edgeId);
    if (!edge) return;
    edge.set('source', args.source);
    edge.set('target', args.target);
    edge.set('sourceHandle', remap(args.sourceHandle));
    edge.set('targetHandle', remap(args.targetHandle));
    edge.set('updatedAt', Date.now());
    written = true;
  }, clientId);
  return written;
}
